import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Button, Tag, Popconfirm, message, Modal, Form, Input } from 'antd'
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  PoweroffOutlined,
  CloudUploadOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import ProTable, { ActionType, ProColumns } from '@ant-design/pro-table'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { deviceApi } from '@api/devices'
import DeviceStatusTag from '@components/DeviceStatusTag'
import { formatTime, formatHandshakeId, getStateColor } from '@utils/format'
import type { Device } from '@app-types/device'

const STORAGE_KEY = 'devices_list_state'

const Devices: React.FC = () => {
  const actionRef = useRef<ActionType>()
  const navigate = useNavigate()
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 })

  // 监听窗口大小变化
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // 从 localStorage 恢复状态
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY)
    if (savedState) {
      try {
        const { keyword, current, pageSize, scrollTop, timestamp } = JSON.parse(savedState)
        // 只恢复5分钟内的状态
        if (timestamp && Date.now() - timestamp < 5 * 60 * 1000) {
          setSearchKeyword(keyword || '')
          setPagination({ current: current || 1, pageSize: pageSize || 20 })
          // 恢复滚动位置
          setTimeout(() => {
            const tableBody = document.querySelector('.ant-table-body')
            if (tableBody && scrollTop) {
              tableBody.scrollTo({ top: scrollTop, behavior: 'auto' })
            }
          }, 200)
        }
        // 清理状态
        localStorage.removeItem(STORAGE_KEY)
      } catch (e) {
        console.error('Failed to restore state:', e)
      }
    }
  }, [])

  // 保存状态的函数
  const saveState = useCallback(() => {
    const tableBody = document.querySelector('.ant-table-body')
    const scrollTop = tableBody?.scrollTop || 0
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      keyword: searchKeyword,
      current: pagination.current,
      pageSize: pagination.pageSize,
      scrollTop,
      timestamp: Date.now(),
    }))
  }, [searchKeyword, pagination])

  // 进入详情页时保存状态
  const handleViewDevice = useCallback((deviceId: string) => {
    saveState()
    navigate(`/devices/${deviceId}`)
  }, [saveState, navigate])

  const { mutate: sendReboot } = useMutation({
    mutationFn: (deviceId: string) => deviceApi.sendCommand(deviceId, 'reboot'),
    onSuccess: () => message.success('重启命令已发送'),
  })

  // 页面卸载时清理状态
  useEffect(() => {
    return () => {
      // 如果是刷新页面，保留状态；如果是进入详情页，状态已保存
    }
  }, [])

  const { mutate: addDevice } = useMutation({
    mutationFn: (deviceId: string) => deviceApi.addDevice(deviceId),
    onSuccess: () => {
      message.success('设备添加成功')
      setAddModalVisible(false)
      actionRef.current?.reload()
    },
  })

  const { mutate: upgradeDevice } = useMutation({
    mutationFn: (deviceId: string) => deviceApi.upgradeDevice(deviceId),
    onSuccess: (data) => {
      if (data.upgrade_started) {
        message.success('OTA升级已启动')
      } else {
        message.info(data.message)
      }
    },
  })

  const { mutate: requestAllStatus } = useMutation({
    mutationFn: () => deviceApi.requestAllStatus(),
    onSuccess: (data) => {
      message.success(`已发送获取全部设备状态请求，检查设备数: ${data.checked_devices}`)
    },
  })

  const { mutate: requestAllConfig } = useMutation({
    mutationFn: () => deviceApi.requestAllConfig(),
    onSuccess: (data) => {
      message.success(`已开始轮询请求 ${data.device_count} 台设备的配置信息`)
    },
  })

  const columns: ProColumns<Device>[] = [
    {
      title: '状态',
      dataIndex: 'is_online',
      valueType: 'select',
      valueEnum: {
        true: { text: '在线', status: 'Success' },
        false: { text: '离线', status: 'Error' },
      },
      render: (_, record) => <DeviceStatusTag isOnline={record.is_online} compact={isMobile} />,
      width: isMobile ? 40 : 80,
      fixed: isMobile ? false : 'left',
      align: 'center',
    },
    {
      title: '设备ID',
      dataIndex: 'device_id',
      copyable: !isMobile,
      ellipsis: true,
      width: isMobile ? 90 : 180,
      fixed: isMobile ? false : 'left',
      render: (_, record) => {
        if (isMobile) {
          // 移动端只显示末尾6位
          const id = record.device_id
          const shortId = id.length > 6 ? '..' + id.slice(-6) : id
          return <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{shortId}</span>
        }
        return record.device_id
      },
    },
    ...(isMobile ? [] : [
      {
        title: '固件版本',
        dataIndex: 'firmware_version',
        render: (val: string) => val || 'N/A',
        width: 120,
      },
      {
        title: '握手ID',
        render: (_: any, record: Device) => {
          const a = record.config_data?.handshake_id_a
          const b = record.config_data?.handshake_id_b
          return formatHandshakeId(a, b)
        },
        width: 100,
      },
    ] as ProColumns<Device>[]),
    {
      title: '信号强度',
      render: (_, record) => record.heartbeat_data?.rssi || 'N/A',
      width: 80,
      hideInTable: isMobile, // 移动端默认隐藏
    },
    ...(isMobile ? [] : [
      {
        title: 'DTU状态',
        dataIndex: ['heartbeat_data', 'dtu_state'],
        render: (val: string) => {
          if (!val) return 'N/A'
          return (
            <Tag color={getStateColor(val)}>
              {val}
            </Tag>
          )
        },
        width: 140,
      },
    ] as ProColumns<Device>[]),
    {
      title: '状态',
      dataIndex: 'upgrade_status',
      render: (val) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          idle: { color: 'default', text: '空闲' },
          pending: { color: 'processing', text: '等待' },
          downloading: { color: 'processing', text: '下载' },
          applying: { color: 'warning', text: '升级' },
          success: { color: 'success', text: '成功' },
          failed: { color: 'error', text: '失败' },
        }
        const { color, text } = statusMap[val as string] || { color: 'default', text: val }
        return <Tag color={color}>{text}</Tag>
      },
      width: isMobile ? 50 : 100,
    },
    ...(isMobile ? [] : [
      {
        title: '最后在线',
        dataIndex: 'last_seen',
        sorter: true,
        render: (val: string) => formatTime(val),
        width: 180,
      },
    ] as ProColumns<Device>[]),
    {
      title: '操作',
      valueType: 'option',
      fixed: isMobile ? false : 'right',
      width: isMobile ? 80 : 200,
      render: (_, record) => [
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDevice(record.device_id)}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? '' : '查看'}
        </Button>,
        <Button
          key="upgrade"
          type="link"
          icon={<CloudUploadOutlined />}
          onClick={() => upgradeDevice(record.device_id)}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? '' : '升级'}
        </Button>,
        <Popconfirm
          key="reboot"
          title="确定要重启设备吗？"
          onConfirm={() => sendReboot(record.device_id)}
        >
          <Button type="link" icon={<PoweroffOutlined />} danger size={isMobile ? 'small' : 'middle'}>
            {isMobile ? '' : '重启'}
          </Button>
        </Popconfirm>,
      ],
    },
  ]

  const handleAddDevice = (values: { device_id: string }) => {
    addDevice(values.device_id)
  }

  return (
    <>
      <ProTable<Device>
        headerTitle="设备列表"
        actionRef={actionRef}
        rowKey="device_id"

        toolBarRender={() => [
          <Button
            key="add"
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => setAddModalVisible(true)}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : '添加设备'}
          </Button>,
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            onClick={() => actionRef.current?.reload()}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : '刷新'}
          </Button>,
          <Button
            key="requestAllStatus"
            icon={<InfoCircleOutlined />}
            onClick={() => requestAllStatus()}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : '获取全部设备信息'}
          </Button>,
          <Button
            key="requestAllConfig"
            icon={<FileTextOutlined />}
            onClick={() => requestAllConfig()}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : '获取全部设备配置'}
          </Button>,
        ]}
        request={async (params) => {
          const res = await deviceApi.getDevices()
          // 保存当前页码
          if (params.current) {
            setPagination({ current: params.current, pageSize: params.pageSize || 20 })
          }
          return {
            data: res.devices,
            success: true,
            total: res.total,
          }
        }}
        search={false}
        toolbar={{
          search: {
            placeholder: '搜索设备ID、固件版本、握手ID...',
            onSearch: (value: string) => {
              setSearchKeyword(value)
            },
            value: searchKeyword,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value
              setSearchKeyword(value)
              // 自动筛选
              if (actionRef.current) {
                actionRef.current.reload()
              }
            },
          },
        }}
        postData={(data: Device[]) => {
          if (!searchKeyword) return data
          const keyword = searchKeyword.toLowerCase()
          return data.filter((device) => {
            const deviceId = device.device_id?.toLowerCase() || ''
            const firmwareVersion = device.firmware_version?.toLowerCase() || ''
            const handshakeA = device.config_data?.handshake_id_a?.toString() || ''
            const handshakeB = device.config_data?.handshake_id_b?.toString() || ''
            const dtuState = device.heartbeat_data?.dtu_state?.toLowerCase() || ''
            return (
              deviceId.includes(keyword) ||
              firmwareVersion.includes(keyword) ||
              handshakeA.includes(keyword) ||
              handshakeB.includes(keyword) ||
              dtuState.includes(keyword)
            )
          })
        }}
        columns={columns}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize: pageSize || 20 })
          },
        }}
        scroll={{ x: isMobile ? 300 : 1200, y: undefined }}
        tableLayout="fixed"
      />

      {/* 添加设备模态框 */}
      <Modal
        title="添加设备"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} onFinish={handleAddDevice} layout="vertical">
          <Form.Item
            name="device_id"
            label="设备ID (IMEI)"
            rules={[{ required: true, message: '请输入设备ID' }]}
          >
            <Input placeholder="请输入设备IMEI" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default Devices
